import React, { memo } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Badge } from "@/shared/components/ui/badge";
import { CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";

const C = {
  card: "#0F1F34",
  cardHover: "#132843",
  border: "rgba(255,255,255,0.08)",
  textPrimary: "#E6EDF7",
  textMuted: "#8FA3BF",
  blue: "#3B82F6",
  emerald: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

const getStatusConfig = (t) => {
  return {
    pending: { icon: Clock, color: C.amber, label: t("pending") },
    processing: { icon: Loader2, color: C.blue, label: t("processing") },
    success: { icon: CheckCircle2, color: C.emerald, label: t("success") },
    failed: { icon: XCircle, color: C.red, label: t("failed") },
    expired: { icon: Clock, color: C.textMuted, label: t("expired") },
    cancelled: { icon: XCircle, color: C.textMuted, label: t("cancelled") },
    declined: { icon: XCircle, color: C.red, label: t("declined") || "Declined" },
  };
};

function RecentTransactions({ transactions = [], onViewAll }) {
  const { t } = useLanguage();
  const statusConfig = getStatusConfig(t);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-6"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <h3 style={{ color: C.textPrimary }} className="text-lg font-semibold">
          {t("recentTransactions")}
        </h3>
        <button
          onClick={onViewAll}
          className="text-sm font-medium hover:underline"
          style={{ color: C.blue }}
        >
          {t("viewAll")}
        </button>
      </div>

      {/* Empty State */}
      {transactions.length === 0 ? (
        <div className="p-8 text-center" style={{ color: C.textMuted }}>
          {t("noTransactionsYet")}
        </div>
      ) : (
        <div className="overflow-x-auto h-80">
          <table className="min-w-full text-sm">
            <thead
              style={{ borderBottom: `1px solid ${C.border}` }}
              className="bg-[#132843]"
            >
              <tr>
                {["paymentId", "Net Amount", "Total Fee", "date", "status"].map((key) => (
                  <th
                    key={key}
                    className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: C.textMuted }}
                  >
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y" style={{ borderColor: C.border }}>
              {transactions.slice(0, 5).map((tx, index) => {
                const statusKey = (tx.status || "").toLowerCase();
                const status = statusConfig[statusKey] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <motion.tr
                    key={tx.id || tx.transaction_id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.03 * index }}
                    whileHover={{
                      backgroundColor: C.cardHover,
                      transition: { duration: 0.2 },
                    }}
                    style={{
                      cursor: "default",
                      borderRadius: 12,
                    }}
                  >
                    <td
                      style={{ color: C.textPrimary }}
                      className="px-6 py-3 whitespace-nowrap text-sm"
                    >
                      <div className="max-w-[180px] truncate">
                        {tx.payment_id || tx.transaction_id || "-"}
                      </div>

                      <span
                        className="px-2 text-[10px] font-semibold rounded-sm"
                        style={{
                          border: `1px solid ${C.border}`,
                          color: C.textMuted,
                        }}
                      >
                        {tx.type || "-"}
                      </span>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold">
                      <span
                        style={{
                          color:
                            tx.type === "Deposit"
                              ? C.blue
                              : tx.type === "Withdrawal"
                                ? C.emerald
                                : C.textPrimary,
                        }}
                      >
                        {tx.type === "WITHDRAWAL" ? `-฿${tx.net_amount || 0}` : `+฿${tx.net_amount || 0}` || "-"}
                      </span>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-sm">
                      <span style={{ color: C.textPrimary }}>{`฿${tx.total_fee || 0}`}</span>
                    </td>

                    <td style={{ color: C.textMuted }} className="px-6 py-3 whitespace-nowrap text-xs">
                      {tx.created_date ? format(new Date(tx.created_date), "yyyy-MM-dd HH:mm") : "-"}
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-xs">
                      <Badge className="gap-1 border-0"
                        style={{
                          backgroundColor: `${status.color}20`,
                          color: status.color,
                        }}
                      >
                        <StatusIcon
                          className={`h-3 w-3 ${tx.status === "PROCESSING" ? "animate-spin" : ""
                            }`}
                        />
                        {status.label}
                      </Badge>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

export default memo(RecentTransactions);
